import {
  CROSS_CLIENT_PREDEFINED_CATEGORIES,
  CROSS_CLIENT_PREDEFINED_ICONS,
  PredefinedCrossClientQuery,
  CrossClientPredefinedCategory,
} from '../../Predefined/crossClientPredefinedQueries'
import classNames from 'classnames'
import React, { useState } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectCrossClientLandingFinishedExecutions,
  selectCrossClientLandingProcessingExecutions,
  selectCrossClientLandingQueuedExecutions,
} from '../../../../store/crossClient/crossClientLanding'
import { CrossClientLandingPredefinedQueries } from '../Predefined/CrossClientLandingPredefinedQueries'
import { CrossClientLandingHistory } from '../History/CrossClientLandingHistory'
import { CrossClientLandingQueued } from '../Queued/CrossClientLandingQueued'
import { CrossClientQueryExecution } from '../../../../models/CrossClient'

type CrossClientPillOption = 'History' | 'Queued' | CrossClientPredefinedCategory

export interface CrossClientLandingFooterProps {
  runDisabled: boolean
  onPredefinedQuerySelect: (crossClientDefault: PredefinedCrossClientQuery) => void
  onRerun: (execution: CrossClientQueryExecution) => void
}

export const CrossClientLandingFooter: React.FC<CrossClientLandingFooterProps> = ({ runDisabled, onPredefinedQuerySelect, onRerun }) => {
  const processingExecutions = useAppSelector(selectCrossClientLandingProcessingExecutions)
  const finishedExecutions = useAppSelector(selectCrossClientLandingFinishedExecutions)
  const queuedExecutions = useAppSelector(selectCrossClientLandingQueuedExecutions)

  const hasHistory = processingExecutions.length || finishedExecutions.length
  const hasQueued = queuedExecutions.length > 0

  const [selectedPill, setSelectedPill] = useState<CrossClientPillOption>(finishedExecutions.length ? 'History' : 'Featured')

  const effectiveSelectedPill: CrossClientPillOption =
    (selectedPill === 'History' && !hasHistory) || (selectedPill === 'Queued' && !hasQueued) ? 'Featured' : selectedPill

  return (
    <div className='d-flex flex-column zocks-gap-2 w-100'>
      <div className='d-flex gap-2 justify-content-center'>
        {hasHistory ? (
          <CrossClientPill
            title='History'
            selected={effectiveSelectedPill === 'History'}
            onClick={() => setSelectedPill('History')}
            icon={'bi-clock-history'}
          />
        ) : null}
        {hasQueued ? (
          <CrossClientPill title='Queued' selected={effectiveSelectedPill === 'Queued'} onClick={() => setSelectedPill('Queued')} icon={'bi-hourglass-split'} />
        ) : null}
        {CROSS_CLIENT_PREDEFINED_CATEGORIES.map((key: CrossClientPredefinedCategory) => {
          const selected = effectiveSelectedPill === key
          return <CrossClientPill key={key} title={key} selected={selected} icon={CROSS_CLIENT_PREDEFINED_ICONS[key]} onClick={() => setSelectedPill(key)} />
        })}
      </div>
      {effectiveSelectedPill === 'History' ? (
        <CrossClientLandingHistory onRerun={onRerun} rerunDisabled={runDisabled} />
      ) : effectiveSelectedPill === 'Queued' ? (
        <CrossClientLandingQueued />
      ) : (
        <CrossClientLandingPredefinedQueries selectedCategory={effectiveSelectedPill} onSelect={onPredefinedQuerySelect} runDisabled={runDisabled} />
      )}
    </div>
  )
}

interface CrossClientPillProps {
  title: string
  selected: boolean
  icon?: string
  onClick: () => void
}

const CrossClientPill: React.FC<CrossClientPillProps> = ({ title, selected, icon, onClick }) => {
  const [hovered, setHovered] = useState<boolean>(false)

  return (
    <div
      className={classNames('d-flex gap-2 px-2 py-1 align-items-center zocks-border-0.5 border border-1 bg-white cross-client-transition', {
        'zocks-text-primary zocks-bg-color-primary-light': selected,
        'zocks-border-color-gray-500': !selected && !hovered,
        'zocks-border-color-primary': selected || hovered,
      })}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role='button'
    >
      {icon ? <i className={`bi ${icon}`} /> : null}

      <div className='paragraph-small medium'>{title}</div>
    </div>
  )
}
